import { MoreHorizOutlined as MoreHorizOutlinedIcon } from '@mui/icons-material';
import Tooltip from '@mui/material/Tooltip';
import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';
import { Fragment, useContext } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import type { EndorsedHardSoftSkill, Endorsement } from '~/shared/data-contract/skill';
import type { TranslationKey } from '~/shared/internationalization/translation';

import { SmallText } from '../../../../design/components/Typography';

type EndorsementKey = Required<keyof Endorsement>;

const endorsementsMap: ReadonlyMap<EndorsementKey, TranslationKey> = new Map([
    ['endorsedByWork', 'pages.user-profile.endorsements.work'],
    ['endorsedByEducation', 'pages.user-profile.endorsements.education'],
    ['endorsedByProject', 'pages.user-profile.endorsements.project'],
    ['endorsedByCourse', 'pages.user-profile.endorsements.course'],
    ['endorsedByCertificate', 'pages.user-profile.endorsements.certificate'],
    ['endorsedByPublication', 'pages.user-profile.endorsements.publication'],
]);

type EndorsementColumn = {
    readonly endorsements: readonly string[];
    readonly key: EndorsementKey;
    readonly translation: TranslationKey;
};

type EndorsementsProps = {
    readonly maxColumns: number;
    readonly skill: EndorsedHardSoftSkill;
};

export const Endorsements: FunctionComponent<EndorsementsProps> = (props) => {
    const { maxColumns, skill } = props;
    const { translate } = useContext(ApplicationContext);

    const availableEndorsementColumns = [...endorsementsMap].reduce((columns, [key, translation]) => {
        const endorsements = skill[key];
        if (is.nonEmptyArray(endorsements)) {
            columns.add({
                endorsements,
                key,
                translation,
            });
        }

        return columns;
    }, new Set<EndorsementColumn>());

    const hasColumnOverflow = availableEndorsementColumns.size > maxColumns;
    const visibleEndorsementColumns = [...availableEndorsementColumns].slice(0, maxColumns);
    const overflowingColumns = hasColumnOverflow ? [...availableEndorsementColumns].slice(maxColumns) : [];
    const overflowingColumnsTooltip = overflowingColumns
        .map((column) => translate(column.translation, { count: column.endorsements.length }))
        .join(', ');

    return (
        <Fragment>
            {visibleEndorsementColumns.map((column) => (
                <SmallText key={column.key} color="secondary">
                    {translate(column.translation, { count: column.endorsements.length })}
                </SmallText>
            ))}
            {hasColumnOverflow && (
                <Tooltip sx={{ mx: 'auto' }} title={overflowingColumnsTooltip}>
                    <MoreHorizOutlinedIcon />
                </Tooltip>
            )}
        </Fragment>
    );
};
